import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useChain } from '../context/ChainContext';
import { mcGetChains, mcGetTokenOverview } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Search, ArrowRight, Clock, X, Loader2, Layers, ChevronLeft } from 'lucide-react';
import { toast } from 'sonner';
import McTrendingPools from '../components/McTrendingPools';

const HERO_BG = 'https://images.unsplash.com/photo-1646800186869-95062dd5008c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGJsb2NrY2hhaW4lMjBuZXR3b3JrJTIwbm9kZXMlMjBkYXJrJTIwM2R8ZW58MHx8fGJsYWNrfDE3ODUyMTU5MDd8MA&ixlib=rb-4.1.0&q=85';

const EVM_ADDR = /^0x[0-9a-fA-F]{40}$/;
const EVM_TX = /^0x[0-9a-fA-F]{64}$/;
const SOL_RE = /^[1-9A-HJ-NP-Za-km-z]{32,44}$/;
const LS_RECENT = 'taiboku-mc.recent';
const QUICK_CHAINS = ['ethereum', 'base', 'solana', 'bsc', 'arbitrum', 'krown'];

function fmtPrice(v) {
  const n = parseFloat(v);
  if (v == null || !isFinite(n) || n <= 0) return '—';
  if (n < 0.01) return `$${n.toFixed(6)}`;
  return `$${n.toLocaleString('en-US', { maximumFractionDigits: 2 })}`;
}
function fmtUsd(v) {
  const n = parseFloat(v);
  if (!isFinite(n)) return '—';
  if (Math.abs(n) >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (Math.abs(n) >= 1_000) return `$${(n / 1_000).toFixed(1)}K`;
  return `$${n.toFixed(0)}`;
}
const trunc = (a, n = 6) => (a ? `${a.slice(0, n + 2)}…${a.slice(-4)}` : '—');
function loadRecent() {
  try { return JSON.parse(localStorage.getItem(LS_RECENT)) || []; } catch { return []; }
}

const ChainChip = ({ chain, active, onClick }) => (
  <button
    type="button"
    data-testid={`chain-chip-${chain.id}`}
    onClick={() => onClick(chain.id)}
    className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-medium transition-[background-color,border-color,transform] duration-200 hover:-translate-y-0.5 ${
      active ? 'border-primary bg-primary text-primary-foreground' : 'border-border bg-card hover:bg-accent'
    }`}
  >
    <span
      className="w-4 h-4 rounded-full flex items-center justify-center text-[8px] font-bold"
      style={chain.accent ? { backgroundColor: `${chain.accent}22`, color: chain.accent } : undefined}
    >
      {(chain.native || chain.label || '?').slice(0, 1)}
    </span>
    {chain.label}
  </button>
);

const ChainCard = ({ chain, selected, onSelect }) => (
  <button
    type="button"
    data-testid={`chain-card-${chain.id}`}
    onClick={() => onSelect(chain.id)}
    className={`text-left border p-4 transition-[background-color,border-color,transform] duration-200 bg-card hover:bg-accent hover:-translate-y-0.5 ${
      selected ? 'border-primary ring-1 ring-primary' : 'border-border'
    }`}
  >
    <div className="flex items-center gap-3">
      <div
        className="w-10 h-10 rounded-full flex items-center justify-center font-bold text-xs shrink-0"
        style={{ backgroundColor: `${chain.accent}22`, color: chain.accent, border: `1px solid ${chain.accent}55` }}
      >
        {(chain.native || '?').slice(0, 3)}
      </div>
      <div className="min-w-0">
        <div className="font-semibold text-sm truncate">{chain.label}</div>
        <div className="text-xs text-muted-foreground font-mono">{chain.native} · {fmtPrice(chain.price_usd)}</div>
      </div>
    </div>
    <div className="mt-3 flex items-center gap-1.5 flex-wrap">
      <Badge variant="outline" className="text-[10px] px-1.5 py-0">{chain.source_label}</Badge>
      {chain.limited && <Badge variant="outline" className="text-[10px] px-1.5 py-0 text-warning border-warning/50">LIMITED</Badge>}
      {chain.id === 'krown' && (
        <Badge variant="outline" className="text-[10px] px-1.5 py-0 text-primary border-primary/50">DASHBOARD →</Badge>
      )}
    </div>
  </button>
);

export default function ExplorerHome() {
  const navigate = useNavigate();
  const { chainParam, setChain } = useChain();
  const [searchParams, setSearchParams] = useSearchParams();
  const [chains, setChains] = useState(null);
  const [query, setQuery] = useState('');
  const [resolving, setResolving] = useState(false);
  const [pair, setPair] = useState(null); // { chain, token, choices: [] }
  const [recent, setRecent] = useState(loadRecent);
  const lastQ = useRef(null);

  const selected = chainParam || 'ethereum';
  const selectedChain = chains?.find((c) => c.id === selected);

  useEffect(() => {
    mcGetChains().then((d) => setChains(d.chains || [])).catch(() => toast.error('Failed to load chains'));
  }, []);

  const pushRecent = (chain, address, type) => {
    const next = [{ chain, address, type }, ...recent.filter((r) => !(r.chain === chain && r.address === address))].slice(0, 8);
    setRecent(next);
    localStorage.setItem(LS_RECENT, JSON.stringify(next));
  };

  const explorerFor = (chainId) => chains?.find((c) => c.id === chainId)?.explorer;

  const resolve = async (raw, chainOverride) => {
    const q = (raw || '').trim();
    if (!q) return;
    setPair(null);

    // Transaction hash → open the chain's block explorer.
    if (EVM_TX.test(q)) {
      const chain = chainOverride || (selected === 'solana' ? 'ethereum' : selected);
      const exp = explorerFor(chain);
      if (exp) { window.open(`${exp}/tx/${q}`, '_blank', 'noopener'); toast.info('Opening transaction in block explorer'); }
      else toast.error('Transaction lookup is not available for this chain yet');
      return;
    }

    const isEvm = EVM_ADDR.test(q);
    const isSol = !isEvm && SOL_RE.test(q);
    if (!isEvm && !isSol) {
      toast.error('Enter a valid token contract, wallet address, or tx hash');
      return;
    }

    let chain = chainOverride || selected;
    if (isSol) chain = 'solana';
    else if (chain === 'solana') { chain = 'ethereum'; toast.info('EVM address — searching on Ethereum'); }

    // Krown data comes from the internal indexer (not DexScreener); route to its
    // dedicated pages. It's just another chain — simply a different data source.
    if (chain === 'krown') {
      pushRecent(chain, q, 'wallet');
      navigate(`/address/${q.toLowerCase()}`);
      return;
    }

    setResolving(true);
    try {
      const ov = await mcGetTokenOverview(chain, q);
      if (ov && ov.indexed && (ov.pool_count || 0) >= 1) {
        pushRecent(chain, q, 'token');
        if ((ov.pool_count || 0) > 1) {
          setPair({ chain, token: ov, choices: ov.pools });
        } else {
          navigate(`/explorer/${chain}/token/${q}`);
        }
      } else {
        pushRecent(chain, q, 'wallet');
        navigate(`/explorer/${chain}/address/${q}`);
      }
    } catch {
      navigate(`/explorer/${chain}/address/${q}`);
    } finally {
      setResolving(false);
    }
  };

  // Auto-resolve a ?q= handed off from the global header search.
  useEffect(() => {
    const q = searchParams.get('q');
    if (q && chains && q !== lastQ.current) {
      lastQ.current = q;
      setQuery(q);
      resolve(q);
      const p = new URLSearchParams(searchParams);
      p.delete('q');
      setSearchParams(p, { replace: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, chains]);

  const onSearch = (e) => { e.preventDefault(); resolve(query); };
  const clearRecent = () => { setRecent([]); localStorage.removeItem(LS_RECENT); };

  // Krown is served by its own internal-indexer dashboard (not the DexScreener
  // search flow), so picking it jumps straight to that dashboard. Every other
  // chain simply becomes the active search scope.
  const pickChain = (id) => {
    if (id === 'krown') { navigate('/dashboard'); return; }
    setChain(id);
  };

  const goPair = (p) => {
    navigate(`/explorer/${pair.chain}/token/${pair.token.address || query.trim()}?pool=${p.pair_address}`);
  };

  return (
    <div data-testid="explorer-home">
      {/* Hero — search-first */}
      <section className="relative -mx-4 sm:-mx-6 lg:-mx-8 -mt-8 px-4 sm:px-6 lg:px-8 overflow-hidden">
        <img
          src={HERO_BG}
          alt=""
          aria-hidden
          className="pointer-events-none absolute inset-0 w-full h-full object-cover opacity-[0.14] dark:opacity-[0.18]"
        />
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-background/40 via-background/70 to-background" />
        <div className="relative max-w-4xl mx-auto py-16 sm:py-24">
          <div className="mono-label mb-3">Multichain Block Explorer</div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-none">
            Search any token,<br className="hidden sm:block" /> wallet or transaction.
          </h1>
          <p className="text-muted-foreground mt-4 text-sm sm:text-base max-w-2xl leading-relaxed">
            Paste a smart-contract address to resolve its trading pairs, liquidity, price and
            market data — or a wallet to trace balances, history, DEX trades and PnL. Across
            {chains ? ` ${chains.length}` : ''} chains.
          </p>

          <form onSubmit={onSearch} className="mt-8 flex flex-col sm:flex-row gap-2" data-testid="explorer-search-form">
            <div className="relative flex-1">
              <Search className="w-5 h-5 absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                data-testid="hero-search-input"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={`Contract / wallet / tx on ${selectedChain?.label || '…'}`}
                className="pl-12 font-mono text-base sm:text-lg h-14 sm:h-16 bg-card/80 backdrop-blur"
                autoFocus
              />
            </div>
            <Button type="submit" data-testid="hero-search-btn" className="h-14 sm:h-16 px-8 text-base" disabled={resolving}>
              {resolving ? <Loader2 className="w-5 h-5 animate-spin" /> : <>Explore <ArrowRight className="w-5 h-5 ml-1" /></>}
            </Button>
          </form>

          {/* Quick chain chips */}
          <div className="mt-4 flex flex-wrap gap-2" data-testid="quick-chains">
            {(chains || []).filter((c) => QUICK_CHAINS.includes(c.id)).map((c) => (
              <ChainChip key={c.id} chain={c} active={selected === c.id} onClick={pickChain} />
            ))}
          </div>
        </div>
      </section>

      <div className="max-w-6xl mx-auto space-y-10 pt-4">
        {/* Pair picker */}
        {pair && (
          <section data-testid="pair-picker">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Layers className="w-5 h-5 text-foreground" />
                <div>
                  <h2 className="text-xl font-bold leading-tight">
                    Select a pair · {pair.token.symbol || trunc(pair.token.address)}
                  </h2>
                  <p className="text-xs text-muted-foreground">{pair.choices.length} pools found on {pair.chain}</p>
                </div>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setPair(null)} data-testid="pair-picker-back">
                <ChevronLeft className="w-4 h-4 mr-1" /> Back
              </Button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {pair.choices.map((p, i) => (
                <button
                  key={`${p.pair_address}-${i}`}
                  data-testid={`pair-option-${i}`}
                  onClick={() => goPair(p)}
                  className="text-left border border-border bg-card hover:bg-accent hover:-translate-y-0.5 transition-[background-color,transform] duration-200 p-4"
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-bold text-sm">{p.base_symbol}/{p.quote_symbol}</span>
                    {p.version && <Badge variant="outline" className="text-[9px] px-1 py-0">{String(p.version).toUpperCase()}</Badge>}
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">{p.dex}</div>
                  <div className="grid grid-cols-3 gap-2 mt-3 text-xs font-mono">
                    <div><div className="text-[10px] text-muted-foreground uppercase tracking-wide">Price</div>{fmtPrice(p.price_usd)}</div>
                    <div><div className="text-[10px] text-muted-foreground uppercase tracking-wide">Liq</div>{fmtUsd(p.liquidity_usd)}</div>
                    <div><div className="text-[10px] text-muted-foreground uppercase tracking-wide">24h Vol</div>{fmtUsd(p.volume_24h)}</div>
                  </div>
                </button>
              ))}
            </div>
          </section>
        )}

        {/* Networks */}
        <section>
          <h2 className="mono-label mb-4">Select network</h2>
          {!chains ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-border">
              {Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-24" />)}
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="chain-grid">
              {chains.map((c) => (
                <ChainCard key={c.id} chain={c} selected={selected === c.id} onSelect={pickChain} />
              ))}
            </div>
          )}
          {selectedChain?.limited && (
            <p className="mt-3 text-xs text-warning" data-testid="chain-limited-note">⚠ {selectedChain.limited}</p>
          )}
        </section>

        {/* Trending + new pools for the selected chain (Krown has its own dashboard) */}
        {selected && selected !== 'krown' && (
          <McTrendingPools chain={selected} chainLabel={selectedChain?.label} />
        )}

        {/* Recent */}
        {recent.length > 0 && (
          <Card data-testid="recent-lookups">
            <CardContent className="pt-5">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2 text-sm font-semibold">
                  <Clock className="w-4 h-4 text-muted-foreground" /> Recent lookups
                </div>
                <Button variant="ghost" size="sm" onClick={clearRecent} data-testid="clear-recent-btn">
                  <X className="w-3.5 h-3.5 mr-1" /> Clear
                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {recent.map((r, i) => (
                  <button
                    key={`${r.chain}-${r.address}`}
                    data-testid={`recent-lookup-${i}`}
                    onClick={() => resolve(r.address, r.chain)}
                    className="px-3 py-1.5 rounded-full border border-border bg-card hover:bg-accent text-xs font-mono transition-colors flex items-center gap-1.5"
                  >
                    {r.type && <span className="text-[9px] uppercase tracking-wide text-muted-foreground">{r.type}</span>}
                    <span className="text-muted-foreground">{r.chain}</span>
                    {trunc(r.address, 5)}
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
